/**
 * The PO line Detail tab's date rows (split from POLineDetailTab, CR-1421):
 * Customer Required Date | Original Required Date (ODD) | Latest Supplier
 * Promise Date, then MRP/ERP Scheduled Date | Payment Terms | Expected
 * Payment Date.
 */
import type { PurchaseOrderLine, POLinePaymentTerms } from '../../lib/api';
import { ReadOnlyField } from './POLineDetailFields';
import {
  CUSTOMER_REQUIRED_DATE, MRP_SCHEDULED_DATE, ORIGINAL_REQUIRED_DATE, SUPPLIER_PROMISE_DATE,
} from '../../config/dateLabels';

interface POLineDateFieldsProps {
  line: PurchaseOrderLine;
  formatDate: (dateString: string) => string;
  getChangeCount: (fieldName: string) => number | null;
}

/**
 * CR-1421: how the expected payment date was reached, e.g. "Promise date
 * 4 Nov 2026 + 30 days" or "Required date 28 Oct 2026, end of month + 30 days".
 *
 * @param terms - the line's payment_terms block; null when no terms are set
 * @param formatDate - the user's date formatter
 * @returns the hint, or undefined when there is nothing to explain
 */
export function paymentDateHint(
  terms: POLinePaymentTerms | null | undefined,
  formatDate: (d: string) => string,
): string | undefined {
  if (!terms?.base_date) return undefined;
  const from = `${terms.basis === 'required' ? 'Required' : 'Promise'} date ${formatDate(terms.base_date)}`;
  const eom = terms.eom ? ', end of month' : '';
  const days = terms.days ? ` + ${terms.days} ${terms.days === 1 ? 'day' : 'days'}` : '';
  return `${from}${eom}${days}`;
}

export function POLineDateFields({ line, formatDate, getChangeCount }: POLineDateFieldsProps) {
  /* The ODD field is the server's odd_date (backend purchasing/line_odd.py):
     the date the on-time verdict, OTIF and arrears measure this line against.
     At PO-line grain that is original_promise_date, else delivery_date; at
     schedule grain it is delivery_date, the active slot's date.

     It used to be sla_delivery_date || delivery_date (CR-4711). The first is
     empty on every tenant, so this showed delivery_date — which on a feed that
     sends a re-planned due date beside the original one is the re-planned
     date, and it restated the Customer Required Date field below. delivery_date is the
     fallback only for a payload that predates odd_date. */
  const agreedDateOdd = line.odd_date ?? line.customer_required_date;
  const terms = line.payment_terms;
  return (
    <>
    {/* Date block. DATE-FIELD-CANON PR 2 (15 Sep 2026): the
        Customer Required Date comes back — CR-4711 dropped it and left
        the MRP/ERP Scheduled Date in the slot legacy used for it, so
        the one date expediting actually moves (an accepted proposal
        lands in the Customer Required Date, API key delivery_date) had no field on this page. Row one is
        the canon: Customer Required Date | Original Required Date (ODD)
        | Latest Supplier Promise Date. The MRP/ERP Scheduled Date is
        KEPT, one row down (an ERP feed fills it). Both roles see
        all four. */}
    <div className="grid grid-cols-3 gap-3">
      <ReadOnlyField
        label={CUSTOMER_REQUIRED_DATE}
        value={line.customer_required_date ? formatDate(line.customer_required_date) : ''}
        badge={getChangeCount('customer_required_date')}
        withCalendarIcon
      />
      <ReadOnlyField
        label={ORIGINAL_REQUIRED_DATE}
        value={agreedDateOdd ? formatDate(agreedDateOdd) : ''}
        withCalendarIcon
      />
      <ReadOnlyField
        label={SUPPLIER_PROMISE_DATE}
        value={line.supplier_promise_date ? formatDate(line.supplier_promise_date) : ''}
        badge={getChangeCount('supplier_promise_date')}
        withCalendarIcon
      />
    </div>
    <div className="grid grid-cols-3 gap-3 mt-3">
      {/* Customer review 11 Sep (item 27a): only the ERP import
          writes this (MrpDate / MRP_Date / ScheduledDate). A dash here
          is a date the customer's feed never sent, not a lost value. */}
      <div title={line.mrp_scheduled_date ? undefined : "Not supplied by this customer's ERP import"}>
        <ReadOnlyField
          label={MRP_SCHEDULED_DATE}
          value={line.mrp_scheduled_date ? formatDate(line.mrp_scheduled_date) : ''}
          withCalendarIcon
        />
      </div>
      {/* CR-1421: the supplier code's terms and when payment falls due, in
          the two slots beside the MRP/ERP date. Both roles. */}
      <ReadOnlyField label="Payment Terms" value={terms?.label} />
      <ReadOnlyField
        label="Expected Payment Date"
        value={terms?.expected_payment_date ? formatDate(terms.expected_payment_date) : ''}
        withCalendarIcon
        hint={paymentDateHint(terms, formatDate)}
      />
    </div>
    </>
  );
}
